import * as React from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { useState, useEffect, useRef } from "react";
import debounce from "lodash/debounce";
import Spinner from "components/common/Spinner";
import IconButton from "./IconButton";
export const SEARCH_DEBOUNCE_THRESHOLD = 300;
const FormContent = styled.div(
  ({ $buttonLeftMargin }) => css`
    > :not(:last-child) {
      margin-right: ${$buttonLeftMargin}px;
    }

    > * {
      display: inline-block;
      vertical-align: top;
      margin-bottom: 5px;
    }
  `
);
const InputFeedback = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  align-items: center;
  min-height: 34px;
  padding-right: 3px;
`;
const StyledContainer = styled.div(
  ({ $topMargin }) => css`
    margin-top: ${$topMargin}px;
  `
);
const StyledInput = styled.input(
  ({ $queryWidth, $feedbackContainerWidth }) => css`
    width: ${$queryWidth}px;
    padding-right: ${$feedbackContainerWidth != null ? $feedbackContainerWidth : 12}px;
  `
);
const Label = styled.label`
  margin-right: 5px;
`;
const InputContainer = styled.div`
  display: inline-block;
  position: relative;
`;
const handleQueryChange = debounce(
  ({
    query,
    onSearch,
    useLoadingState,
    setLoadingState,
    resetLoadingState
  }) => {
    if (useLoadingState) {
      setLoadingState().then(() => {
        onSearch(query, resetLoadingState);
      });
    } else {
      onSearch(query);
    }
  },
  SEARCH_DEBOUNCE_THRESHOLD
);
const SearchForm = ({
  useLoadingState = false,
  queryHelpComponent = null,
  queryWidth = 400,
  focusAfterMount = false,
  children = null,
  className = "",
  placeholder = void 0,
  buttonLeftMargin = 5,
  label = null,
  onReset = null,
  onSearch = null,
  wrapperClass = "search",
  topMargin = 0,
  onQueryChange = void 0,
  query: propsQuery = ""
}) => {
  var _a;
  const { t } = useTranslation("common");
  const [query, setQuery] = useState(propsQuery);
  const [isLoading, setIsLoading] = useState(false);
  const inputFeedbackContainer = useRef(void 0);
  useEffect(() => {
    setQuery(propsQuery);
  }, [propsQuery]);
  const setLoadingState = () => new Promise((resolve) => {
    if (useLoadingState) {
      setIsLoading(true);
      resolve();
    } else {
      resolve();
    }
  });
  const resetLoadingState = () => {
    if (useLoadingState) {
      setIsLoading(false);
    }
  };
  const handleReset = () => {
    resetLoadingState();
    setQuery(propsQuery);
    if (typeof onQueryChange === "function") {
      onQueryChange(propsQuery);
    }
    if (typeof onReset === "function") {
      onReset();
    }
  };
  const onChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newQuery = e.target.value;
    setQuery(newQuery);
    if (typeof onQueryChange === "function") {
      onQueryChange(newQuery);
    }
    if (typeof onSearch === "function") {
      handleQueryChange({
        query: newQuery,
        onSearch,
        useLoadingState,
        setLoadingState,
        resetLoadingState
      });
    }
  };
  return /* @__PURE__ */ React.createElement(StyledContainer, { className: `${wrapperClass} ${className}`, $topMargin: topMargin }, /* @__PURE__ */ React.createElement(FormContent, { $buttonLeftMargin: buttonLeftMargin }, /* @__PURE__ */ React.createElement("div", { className: "form-group" }, label && /* @__PURE__ */ React.createElement(Label, { htmlFor: "common-search-form-query-input", className: "control-label" }, label), /* @__PURE__ */ React.createElement(InputContainer, { className: "input-container" }, /* @__PURE__ */ React.createElement(
    StyledInput,
    {
      id: "common-search-form-query-input",
      autoFocus: focusAfterMount,
      onChange,
      value: query,
      placeholder: placeholder != null ? placeholder : t("search_form.placeholder"),
      type: "text",
      $queryWidth: queryWidth,
      className: "query form-control",
      autoComplete: "off",
      spellCheck: "false",
      $feedbackContainerWidth: (_a = inputFeedbackContainer.current) == null ? void 0 : _a.scrollWidth
    }
  ), /* @__PURE__ */ React.createElement(InputFeedback, { ref: inputFeedbackContainer }, isLoading && /* @__PURE__ */ React.createElement(Spinner, { text: "" }), query && typeof onReset === "function" && /* @__PURE__ */ React.createElement(IconButton, { name: "close", title: t("actions.reset_search"), onClick: handleReset }), queryHelpComponent))), children));
};
export default SearchForm;
